/* eslint-disable @typescript-eslint/member-ordering */
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ColDef, GridApi, GridReadyEvent, RowNode } from 'ag-grid-community';
import { SessionService } from '../../../auth/session/session.service';
import { getDefaultDate } from '../../device-monitoring-search/device-monitoring-search.component';
import {
  DeviceEvent,
  MonitoringService,
} from '../../services/monitoring.service';
import { Triggers } from '../triggers';

@Component({
  selector: 'dcc-device-event-modal',
  templateUrl: './device-event-modal.component.html',
  styleUrls: ['./device-event-modal.component.scss'],
})
export class DeviceEventModalComponent {
  @Output() refreshPage: EventEmitter<any> = new EventEmitter();
  @Output() closePage: EventEmitter<any> = new EventEmitter();
  private gridApi!: GridApi<DeviceEvent>;
  events: DeviceEvent[] = [];
  @Input() serialNumber: string;
  startDate: string = getDefaultDate(
    new Date(new Date().getTime() - 14 * 86400000),
  );
  endDate: string = getDefaultDate(new Date());
  columnDefs: ColDef[] = [
    {
      maxWidth: 150,
      headerName: 'DCE LOCATION',
      checkboxSelection: (params) => this.canRequestEvent(params.data),
      valueGetter: (params) => this.getDceLocation(params.data),
    },
    {
      field: 'triggerTime',
      headerName: 'EVENT DATE',
      valueFormatter: (params) => new Date(params.value).toLocaleString(),
    },
    {
      field: 'triggerType',
      headerName: 'TRIGGER',
      valueFormatter: (params) => Triggers[params.value].replace(/_/g, ' '),
    },
    {
      field: 'viiActor',
      headerName: 'REQUESTER',
    },
    {
      field: 'viiRequestDate',
      headerName: 'REQUEST DATE',
      valueFormatter: (params) =>
        params.value ? new Date(params.value).toLocaleString() : '',
    },
  ];

  defaultColDef: ColDef = {
    sortable: true,
    filter: false,
    resizable: true,
    suppressMovable: true,
  };

  canRequestEvents = this.sessionService
    .getCurrentSession()
    .groups.includes('DeviceCommandWriter'); // update to other role once rolled out
  userName = this.sessionService.getCurrentSession().name;

  isRowSelectable = (rowNode: RowNode<DeviceEvent>) =>
    this.canRequestEvent(rowNode.data);

  constructor(
    private activeModal: NgbActiveModal,
    private monitoringService: MonitoringService,
    private sessionService: SessionService,
  ) {}

  private getDceLocation(event: DeviceEvent): string {
    if (event.safetyCustomerEventId) {
      return 'In Cloud';
    }

    // request but upload not complete
    if (event.viiRequestId) {
      return 'Requested';
    }

    return 'On Device';
  }

  private canRequestEvent(event: DeviceEvent): boolean {
    return (
      this.canRequestEvents &&
      !event.safetyCustomerEventId &&
      !event.viiRequestId &&
      !event.shouldIngestEvent
    );
  }

  private async loadEvents(): Promise<void> {
    this.gridApi.showLoadingOverlay();
    const eventResponse = await this.monitoringService.getDeviceEvents(
      this.serialNumber,
      new Date(this.startDate),
      new Date(this.endDate),
    );
    this.events = eventResponse.deviceEvents;
    this.gridApi.setRowData(this.events);
    this.gridApi.hideOverlay();
  }

  async requestEvents(): Promise<void> {
    const selectedEvents = this.gridApi.getSelectedRows();

    const selectedEventIds = selectedEvents.map((e) => e.id);

    const requestEventsResponse = await this.monitoringService.requestEvents({
      docIds: selectedEventIds,
      actor: this.userName,
    });

    selectedEvents.forEach(
      (e) => (e.viiRequestId = requestEventsResponse.viiRequestId),
    );

    this.gridApi.setRowData(this.events);

    this.gridApi.deselectAll();
  }

  onDateChange(): Promise<void> {
    return this.loadEvents();
  }

  onGridReady(params: GridReadyEvent): Promise<void> {
    this.gridApi = params.api;

    return this.loadEvents();
  }

  close() {
    this.activeModal.close(false);
  }

  isRequestEventsEnabled(): boolean {
    return !!this.gridApi?.getSelectedRows()?.length;
  }
}
